import { EFinancingCompany } from '@if/core/types'
import type { AuthConfig, IfUser } from '@if/mui'
import { reportErrorToServer } from '@src/services/error-logger'
import { CLEAR_CACHE_EXCEPTION_KEY, SELECTED_COMPANY_STORAGE_KEY } from '@src/types/constants'

declare module '@if/mui' {
  interface IfUserRights {
    canAddUser: boolean
    canEditUser: boolean
    canForceUnlockLoan: boolean
    canMoveCalendarWithLateAmount: boolean
    canEditLockedPpaPayments: boolean
    canExecuteDevTools: boolean
    canPerformChargeOff: boolean
    canPerformProductRefund: boolean
  }

  interface IfUser {
    username: string
    langId: string
    roles: string[]
    isInternal: boolean
    companyIds: EFinancingCompany[]
  }
}

const apiUrl = (path: string) => `${import.meta.env.VITE_API_URL}${path}`

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function csv(value: unknown): string[] {
  const raw = text(value)
  return raw.length > 0 ? raw.split(',') : []
}

// Spelled out one right per line so a permission the backend does not grant reads
// `false` rather than `undefined`.
function buildRights(claim: unknown): IfUser['rights'] {
  const granted = new Set(csv(claim))
  return {
    canAddUser: granted.has('canAddUser'),
    canEditUser: granted.has('canEditUser'),
    canForceUnlockLoan: granted.has('canForceUnlockLoan'),
    canMoveCalendarWithLateAmount: granted.has('canMoveCalendarWithLateAmount'),
    canEditLockedPpaPayments: granted.has('canEditLockedPpaPayments'),
    canExecuteDevTools: granted.has('canExecuteDevTools'),
    canPerformChargeOff: granted.has('canPerformChargeOff'),
    canPerformProductRefund: granted.has('canPerformProductRefund'),
  }
}

/** Drop the app-owned state scoped to the identity being replaced, keeping device preferences. */
function clearAppState(): void {
  const preserved: Record<string, string> = {}
  CLEAR_CACHE_EXCEPTION_KEY.forEach((key) => {
    const value = localStorage.getItem(key)
    if (value) preserved[key] = value
  })
  localStorage.clear()
  Object.entries(preserved).forEach(([key, value]) => localStorage.setItem(key, value))
}

function clearIdentityState(): void {
  localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY)
}

export const authConfig: AuthConfig = {
  kind: 'entra',
  clientId: import.meta.env.VITE_REGISTRATION_CLIENT_ID,
  authority: 'https://login.microsoftonline.com/fc1a2105-01a3-457f-9dd7-3ef433ad1a9e',
  apiScopes: [`api://${import.meta.env.VITE_REGISTRATION_CLIENT_ID}/ReadAccess`],
  exchangeEndpoint: apiUrl('/GenerateJwtToken'),
  mockLoginEndpoint: apiUrl('/MockLogin'),
  onLogout: clearAppState,
  onMockLogin: clearIdentityState,
  onLogError: (message) => reportErrorToServer(new Error(message)),
}

/** `firstName` / `lastName` are not claims, so they are split off the full name. */
export function mapClaims(payload: Record<string, unknown>): IfUser {
  const name = text(payload.name)
  const [firstName = '', ...rest] = name.split(' ')

  return {
    id: text(payload.id),
    name,
    firstName,
    lastName: rest.join(' '),
    email: text(payload.email),
    username: text(payload.username),
    langId: text(payload.langId),
    roles: csv(payload.roles),
    isInternal: payload.isInternal === 'true',
    companyIds: csv(payload.companyIds) as EFinancingCompany[],
    rights: buildRights(payload.rights),
  }
}
